/*
 * Terms and Conditions Component
 *
 * Comprehensive legal terms for the Belmopan City Council Garbage Truck Tracker
 * Includes GPS accuracy disclaimers, schedule change disclaimers, and "as is" usage terms
 */

import React from 'react';

const TermsAndConditions = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-full md:h-auto md:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-blue-600 text-white p-6 rounded-t-lg flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold flex items-center">
                <span className="mr-3">📋</span>
                Terms and Conditions
              </h2>
              <p className="text-blue-100 mt-1">
                Belmopan City Council - Garbage Truck Tracker
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">{/* This ensures the content area is scrollable */}
          <div className="space-y-8">
            
            {/* Last Updated */}
            <div className="text-center text-sm text-gray-500">
              <p>Last Updated: {new Date().toLocaleDateString()}</p>
            </div>

            {/* Introduction */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-4">1. Introduction</h3>
              <p className="text-gray-700 leading-relaxed">
                Welcome to the Belmopan City Council Garbage Truck Tracker ("the Application", "we", "our", "us").
                These Terms and Conditions ("Terms") govern your use of our garbage collection route tracking
                application and services. By accessing or using the Application, you agree to be bound by these Terms.
                If you do not agree to these Terms, please do not use the Application.
              </p>
            </section>

            {/* Service Description */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-4">2. Service Description</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                The Belmopan City Council Garbage Truck Tracker is a real-time GPS tracking application that provides:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Real-time garbage truck location tracking</li>
                <li>Garbage collection route monitoring</li>
                <li>Proximity-based notifications when trucks approach your area</li>
                <li>Collection schedule awareness for Belmopan residents</li>
              </ul>
            </section>

            {/* Schedule Disclaimer */}
            <section className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">🚛</span>
                3. Garbage Collection Schedule Disclaimer
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>IMPORTANT NOTICE REGARDING COLLECTION SCHEDULES:</strong> Garbage collection times and routes
                displayed in this Application are estimates and may change without prior notice due to various factors including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Adverse weather conditions (heavy rain, flooding, storms)</li>
                <li>Traffic congestion and road conditions</li>
                <li>Equipment breakdowns or maintenance requirements</li>
                <li>Public holidays and special events</li>
                <li>Operational adjustments and route optimization</li>
                <li>Emergency situations or unforeseen circumstances</li>
                <li>Staffing considerations</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>The Belmopan City Council reserves the right to modify collection schedules and routes
                at any time. Users should not rely solely on this Application for waste disposal timing.</strong>
              </p>
            </section>

            {/* AS IS Disclaimer */}
            <section className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">⚠️</span>
                4. "AS IS" Usage Disclaimer
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>THE APPLICATION IS PROVIDED "AS IS" AND "AS AVAILABLE" BASIS.</strong> We make no 
                representations or warranties of any kind, express or implied, including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>The accuracy, completeness, or reliability of the information provided</li>
                <li>The availability or uninterrupted operation of the Application</li>
                <li>The suitability of the Application for any particular purpose</li>
                <li>The absence of errors, viruses, or other harmful components</li>
              </ul>
            </section>

            {/* GPS Accuracy Disclaimer */}
            <section className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">📍</span>
                5. GPS Accuracy and Limitations
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>IMPORTANT GPS ACCURACY NOTICE:</strong> Due to the inherent nature of GPS technology, 
                location data provided by this Application may have an accuracy variance of approximately 
                <strong> 15 feet (4.5 meters) in any direction</strong> from the actual position.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                GPS accuracy can be affected by various factors including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Atmospheric conditions and weather</li>
                <li>Satellite signal interference</li>
                <li>Urban environments with tall buildings</li>
                <li>Indoor or underground locations</li>
                <li>Device hardware limitations</li>
                <li>Network connectivity issues</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>You acknowledge and accept that location data should not be solely relied upon for 
                critical decisions, emergency situations, or precise navigation purposes.</strong>
              </p>
            </section>

            {/* Liability Limitations */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-4">6. Limitation of Liability</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Any direct, indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of profits, data, or business opportunities</li>
                <li>Damages arising from reliance on inaccurate location information</li>
                <li>Service interruptions or downtime</li>
                <li>Third-party actions or content</li>
                <li>Equipment damage or malfunction</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                You use the Application at your own risk and assume full responsibility for any consequences 
                arising from such use.
              </p>
            </section>

            {/* User Responsibilities */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-4">7. User Responsibilities</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                By using the Application, you agree to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Provide accurate and up-to-date information</li>
                <li>Use the Application in compliance with all applicable laws</li>
                <li>Respect the privacy and rights of other users</li>
                <li>Not interfere with or disrupt the Application's functionality</li>
                <li>Not use the Application for illegal or unauthorized purposes</li>
                <li>Maintain the security of your account credentials</li>
              </ul>
            </section>

            {/* Privacy and Data */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-4">8. Privacy and Data Collection</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We collect and process location data and other information to provide our services. 
                Key points about data handling:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Location data is collected only when you use notification features</li>
                <li>We implement reasonable security measures to protect your data</li>
                <li>Your notification preferences are stored to provide timely alerts</li>
                <li>You can request data deletion by contacting support</li>
              </ul>
            </section>

            {/* Service Availability */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-4">9. Service Availability</h3>
              <p className="text-gray-700 leading-relaxed">
                <strong>We do not guarantee continuous, uninterrupted, or error-free availability of the Application.</strong> 
                The service may be temporarily unavailable due to maintenance, technical issues, or circumstances 
                beyond our control. We reserve the right to modify, suspend, or discontinue any part of the 
                Application at any time without prior notice.
              </p>
            </section>

            {/* Intellectual Property */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-4">10. Intellectual Property</h3>
              <p className="text-gray-700 leading-relaxed">
                The Application and its content are protected by copyright, trademark, and other intellectual 
                property laws. You may not copy, modify, distribute, or reverse engineer any part of the 
                Application without our express written permission.
              </p>
            </section>

            {/* Termination */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-4">11. Termination</h3>
              <p className="text-gray-700 leading-relaxed">
                We may terminate or suspend your access to the Application immediately, without prior notice, 
                if you violate these Terms. Upon termination, your right to use the Application will cease 
                immediately, and we may delete your account and associated data.
              </p>
            </section>

            {/* Changes to Terms */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-4">12. Changes to Terms</h3>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify users of material 
                changes through the Application or by email. Your continued use of the Application after 
                such changes constitutes acceptance of the new Terms.
              </p>
            </section>

            {/* Governing Law */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-4">13. Governing Law</h3>
              <p className="text-gray-700 leading-relaxed">
                These Terms are governed by and construed in accordance with the laws of the jurisdiction 
                where our company is incorporated, without regard to conflict of law principles.
              </p>
            </section>

            {/* Contact Information */}
            <section>
              <h3 className="text-xl font-bold text-gray-900 mb-4">14. Contact Information</h3>
              <p className="text-gray-700 leading-relaxed">
                If you have any questions about these Terms and Conditions, please contact us through the 
                "Contact Us" feature in the Application or reach out to our support team.
              </p>
            </section>

            {/* Acknowledgment */}
            <section className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">✅</span>
                Acknowledgment
              </h3>
              <p className="text-gray-700 leading-relaxed">
                <strong>By using the Belmopan City Council Garbage Truck Tracker application, you acknowledge
                that you have read, understood, and agree to be bound by these Terms and Conditions.</strong> You
                also acknowledge that garbage collection schedules may change due to external factors, and you
                accept the limitations and disclaimers regarding GPS accuracy and service availability.
              </p>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              © {new Date().getFullYear()} Belmopan City Council. All rights reserved.
            </p>
            <button
              onClick={onClose}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              I Understand
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;